import * as React from 'react';
import { Prism } from 'prism-react-renderer';
import { useDispatch, useSelector } from 'react-redux';
import { useId } from '@fluentui/react-components';
import { Toaster, Toast, ToastBody, ToastTitle, useToastController } from '@fluentui/react-components';
import {
    RootState,
    setScriptMessage,
    addPseudoMessage,
    setProcessing,
    setSessionId,
    setSessionOwner,
    setIsViewingOthersSession,
    setSessionDataset,
    setDataset,
    setAnalyticsMode,
    setSessionMode,
    selectEffectiveAnalytics,
    changeSetting,
} from '../../utils/store';
import {
    CopilotService,
    type ChatSegmentResponse,
    type ContextItem,
} from '../../Service/ServerAPI';
import { useTokenService } from '../../tokenService'; // Import your token service hook

import { AppInsightsContext } from '../../utils/appInsights';
import { useUser } from '../../utils/UserContext';
import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Acrylic from '../Acrylic/Acrylic';
import { makeStyles, Text } from '@fluentui/react-components';
import ChatUI from '../Chat/ChatUI';
import { useHubManager } from '../../Service/SignalR/useHubManager';
import { HubManager } from '../../Service/SignalR/HubManager';
import { getSessionUrl } from '../../utils/sessionUrl';

(typeof global !== 'undefined' ? global : window).Prism = Prism;
require('prismjs/components/prism-lua');

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '100%',
        position: 'relative',
        overflowY: 'hidden',
        overscrollBehavior: 'contain',
    },
    contentContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh !important',
    },
    chatContainer: {
        flexGrow: 1,
        width: '100%',
        height: '100%',
    },
    footer: {
        textAlign: 'center',
        color: '#fff',
        fontSize: '14px',
        fontFamily: 'Segoe UI',
        fontWeight: '400',
        wordWrap: 'break-word',
        zIndex: 2,
        position: 'relative',
        opacity: '0.75',
        bottom: '10px',
    },
});

interface GPTProps {
    isAuthed: boolean,
    showScreen: boolean,
    setShowScreen: (showScreen: boolean) => void;
}

const CampaignGPT: React.FC<GPTProps> = ({isAuthed, showScreen, setShowScreen}) =>
{
    const classes = useStyles();
    const dispatch = useDispatch();
    const [, setLuaScript] = React.useState('');
    const [, setLoadingPseudocode] = React.useState(true);
    const [loadingSessionId, setLoadingSessionId] = React.useState<string | null>(null);
    const loadingSessionRef = React.useRef<string | null>(null);
    const navigate = useNavigate();
    const settings = useSelector((state: RootState) => state.settings);
    const tokenService = useTokenService();
    const copilotService = CopilotService.getInstance(tokenService);

    useHubManager(); // Initialize HubManager to sync with apiEndpoint settings

    const query = useQuery();
    const sessionIdFromURL = query.get('sessionId');
    const endpointFromURL = query.get('endpoint');
    const modeFromURL = query.get('mode');

    const endpointToasterId = useId('endpoint-toast');
    const { dispatchToast } = useToastController(endpointToasterId);

    // Apply endpoint from URL on initial load
    React.useEffect(() => {
        if (endpointFromURL && endpointFromURL !== settings.apiEndpoint) {
            dispatch(changeSetting({ key: 'apiEndpoint', value: endpointFromURL }));
            dispatchToast(
                <Toast>
                    <ToastTitle>Endpoint Updated</ToastTitle>
                    <ToastBody>Connected to: {endpointFromURL}</ToastBody>
                </Toast>,
                { intent: 'info', timeout: 5000 }
            );
        }
    }, [endpointFromURL]);

    // Apply analytics mode from URL on initial load
    React.useEffect(() => {
        if (modeFromURL === 'analytics' && !settings.analyticsMode) {
            dispatch(setAnalyticsMode(true));
        }
    }, [modeFromURL]);

    // Sync apiEndpoint to URL
    React.useEffect(() => {
        if (settings.apiEndpoint) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('endpoint', settings.apiEndpoint);
            navigate(`${currentUrl.pathname}${currentUrl.search}`, { replace: true });
        }
    }, [settings.apiEndpoint, navigate]);

    // Sync sessionId and analyticsMode to URL
    React.useEffect(() => {
        navigate(getSessionUrl(
            window.location.href,
            settings.sessionId,
            settings.analyticsMode
        ), {
            replace: true,
        });
    }, [settings.sessionId, settings.analyticsMode, navigate]);

    React.useEffect(() => {
        // Check if the sessionId parameter is present in the URL
        if (sessionIdFromURL && sessionIdFromURL !== settings.sessionId) {
            // Dispatch an action to set the sessionId in the Redux store
            loadSession(sessionIdFromURL);
        }
    }, [dispatch, sessionIdFromURL]);

    // New state to trigger the API call
    const [apiTrigger, setApiTrigger] = React.useState({
        trigger: false,
        userInput: '',
        localPseudocode: '',
    });

    // Effect to call the API when apiTrigger changes
    React.useEffect(() => {
        if (apiTrigger.trigger) {
            setLoadingPseudocode(true);
            
            // Attempt to ensure SignalR connection before chat request
            const hubManager = HubManager.getInstance(tokenService);
            hubManager.ensureConnection(settings.apiEndpoint)
                .catch(error => {
                    // Connection attempt failed, but continue with API call
                    // The app will work without real-time features
                    console.log('SignalR connection could not be established, continuing without real-time features');
                });
            
            // Use the session-locked dataset only when it belongs to an active session.
            // If there's no active sessionId, fall back to the currently selected dataset
            // to avoid leaking a previously-locked session dataset into a new conversation.
            const datasetForRequest = settings.sessionId
                ? (settings.sessionDataset || settings.dataset)
                : settings.dataset;

            // Active session's locked mode wins; otherwise fall back to the global toggle.
            const effectiveAnalytics = selectEffectiveAnalytics(settings);

            // Build chat history for analytics context
            const analyticsChatHistory = effectiveAnalytics
                ? settings.chat
                    .filter(msg => msg.role === 'User' || msg.role === 'Assistant')
                    .map(msg => {
                        if (msg.role === 'User') {
                            return { role: 'user', content: (msg as any).userInput || '' };
                        }
                        const response = msg as any;
                        let content = response.responseText || '';
                        // Append ID mappings as context for follow-up queries
                        if (response.idMapping && Object.keys(response.idMapping).length > 0) {
                            const mappings = Object.entries(response.idMapping)
                                .map(([id, name]) => `${id}=${name}`)
                                .join(', ');
                            content += `\n\n[Known ID mappings: ${mappings}]`;
                        }
                        return { role: 'assistant', content };
                    })
                : undefined;

            const contextArray: ContextItem[] = Object.keys(settings.context).map(
                (key) => settings.context[key]
            );
            const dimensionContextJson = contextArray.length > 0 ? JSON.stringify(contextArray) : undefined;

            // Call getAccessToken to get the token before making the API call
            const apiCall = effectiveAnalytics
                ? copilotService.AnalyticsAPI(
                    apiTrigger.userInput,
                    settings.apiEndpoint,
                    datasetForRequest,
                    analyticsChatHistory,
                    dimensionContextJson,
                    settings.sessionId || undefined,
                    (description, script) => {
                        // Phase 1 complete — show interim message while script executes
                        dispatch(addPseudoMessage({
                            role: 'Assistant',
                            sessionId: settings.sessionId,
                            endpoint: 'analytics',
                            responseText: `**Approach:** ${description}\n\n*Executing analysis across all shards...*`,
                            unparsedGptResponse: '',
                            script: script,
                        } as any));
                    }
                )
                : copilotService.SegmentCombinedAPI(
                    apiTrigger.userInput,
                    settings.apiEndpoint,
                    settings.pseudocodeChat,
                    settings.sessionId,
                    apiTrigger.localPseudocode,
                    undefined,
                    datasetForRequest
                );
            
            apiCall
                .then((response) => {
                    if (response.sessionId) {
                        dispatch(setSessionId(response.sessionId));
                    }
                    dispatch(setSessionOwner(user?.userProfile?.userName ?? ''));
                    dispatch(setIsViewingOthersSession(false));
                    // Lock the session dataset on the first message
                    if (!settings.sessionId && settings.dataset) {
                        dispatch(setSessionDataset(settings.dataset));
                    }
                    handlePseudocodeReturn(apiTrigger.userInput, response);
                })
                .catch((error) => {
                    dispatch(
                        setScriptMessage([
                            ...settings.chat,
                            {
                                role: 'Error',
                                sessionId: '',
                                message: error,
                                endpoint: '',
                                length: 0,
                                status: 500,
                            },
                        ])
                    );
                })
                .finally(() => {
                    // Reset the trigger
                    setApiTrigger({ ...apiTrigger, trigger: false });
                });
        }
    }, [apiTrigger]);

    const onLuaScriptChange = (code: string) => {
        setLuaScript(code);
    };

    const appInsights = useContext(AppInsightsContext);

    const user = useUser();

    const handlePseudocodeReturn = (
        input: string,
        response: ChatSegmentResponse | null
    ) => {
        if (response) {
            if (response.segment) {
                dispatch(addPseudoMessage(response));

                appInsights.trackEvent(
                    { name: 'SegmentRun' },
                    {
                        user: user?.userProfile?.userName,
                        initialPrompt: input,
                        response: response.responseText,
                        Pseudocode: response.segment,
                        LuaScript: response?.script,
                        executionTimeMs: response?.executeTimeMs,
                        users: response?.users,
                        exception: response?.exception,
                        scriptError: response?.script_error,
                        sessionId: response.sessionId,
                        endpoint: settings.apiEndpoint,
                    }
                );
            } else {
                dispatch(addPseudoMessage(response));
                appInsights.trackEvent(
                    { name: 'SegmentRun' },
                    {
                        user: user?.userProfile?.userName,
                        initialPrompt: input,
                        response: response.responseText,
                        Pseudocode: response?.segment,
                        LuaScript: response?.script,
                        executionTimeMs: response?.executeTimeMs,
                        users: response?.users,
                        exception: response?.exception,
                        scriptError: response?.script_error,
                        sessionId: response.sessionId,
                        endpoint: settings.apiEndpoint,
                    }
                );
            }
        }
        setLoadingPseudocode(false);
        dispatch(setProcessing(false));
    };

    function updatePseudocode(userInput: string, localPseudocode: string) {
        // Lock this session's mode on its first message so rendering/sending stay consistent.
        if (!settings.sessionMode) {
            dispatch(setSessionMode(settings.analyticsMode ? 'analytics' : 'segmentation'));
        }
        dispatch(
            setScriptMessage([
                ...settings.chat,
                {
                    userInput: userInput,
                    sessionId: settings.sessionId,
                    role: 'User',
                    endpoint: 'chat',
                },
            ])
        );
        setApiTrigger({ trigger: true, userInput, localPseudocode });
    }

    function loadSession(sessionId: string) {
        window.scrollTo(0, 0);
        setLoadingSessionId(sessionId);
        loadingSessionRef.current = sessionId;
        let owner: string = '';
        copilotService
            .findSession(sessionId, settings.apiEndpoint)
            .then((response) => {
                if (loadingSessionRef.current !== sessionId) return;
                if (response && response.length > 0) {
                    owner = response[0];
                    copilotService
                        .getSession(
                            response[0],
                            sessionId,
                            settings.apiEndpoint
                        )
                        .then((response) => {
                            if (loadingSessionRef.current !== sessionId) return;
                            dispatch(setScriptMessage(response.$values));
                            // Identify the loaded session's mode from its persisted messages so it always renders correctly.
                            const isAnalyticsSession = Array.isArray(response.$values) &&
                                response.$values.some((e: any) => e?.endpoint === 'analytics');
                            dispatch(setSessionMode(isAnalyticsSession ? 'analytics' : 'segmentation'));
                            dispatch(setSessionId(sessionId));
                            dispatch(setSessionOwner(owner));
                            const currentUsername = user?.userProfile?.userName;
                            const isOthers = !!(owner && currentUsername && owner !== currentUsername);
                            dispatch(setIsViewingOthersSession(isOthers));
                            if (!owner || !currentUsername) {
                                console.warn(`Session owner check: owner=${owner || 'NULL'}, currentUser=${currentUsername || 'NULL'} — one or both are missing`);
                            }
                            const chatMetadata = response.$values?.[0];
                            const sessionDatasetValue = chatMetadata?.dataset || settings.dataset || 'pdm';
                            dispatch(setSessionDataset(sessionDatasetValue));
                            if (sessionDatasetValue !== settings.dataset) {
                                dispatch(setDataset(sessionDatasetValue));
                                const dsDisplayName = settings.availableDatasets.find(d => d.name === sessionDatasetValue)?.displayName || sessionDatasetValue;
                                dispatchToast(
                                    <Toast>
                                        <ToastTitle>Dataset Changed</ToastTitle>
                                        <ToastBody>Switched to: {dsDisplayName}</ToastBody>
                                    </Toast>,
                                    { intent: 'info', timeout: 4000 }
                                );
                            }
                            setShowScreen(false);
                            showScreen=false;
                            setLoadingSessionId(null);
                        });
                } else {
                    if (loadingSessionRef.current !== sessionId) return;
                    console.log(`Session ${sessionId} not found, creating new chat session`);
                    dispatch(setSessionId(sessionId));
                    dispatch(setSessionOwner(user?.userProfile?.userName ?? ''));
                    dispatch(setIsViewingOthersSession(false));
                    dispatch(setScriptMessage([]));
                    dispatch(setSessionMode(''));
                    setShowScreen(true);
                    showScreen=true;
                    setLoadingSessionId(null);
                }
            })
            .catch(() => {
                if (loadingSessionRef.current === sessionId) {
                    setLoadingSessionId(null);
                }
            });
    }

    function cloneSession() {
        copilotService
            .copySession(
                user?.userProfile?.userName ?? 'SharedSessions',
                settings.sessionOwner,
                settings.sessionId,
                settings.apiEndpoint
            )
            .then((response) => {
                if (response) {
                    dispatch(setSessionId(response.toString()));
                    dispatch(setSessionOwner(user?.userProfile?.userName ?? ''));
                    dispatch(setIsViewingOthersSession(false));
                    setShowScreen(false);
                    showScreen=false;
                }
            });
            
    }

    return (
        <React.Fragment>
            <Toaster toasterId={endpointToasterId} position="top-end" />
            <div className={classes.root}>
                <Acrylic theme="dark" />
                <div className={classes.contentContainer}>
                    <div className={classes.chatContainer}>
                       
                        <ChatUI
                            sendMessage={updatePseudocode}
                            loadSession={loadSession} 
                            cloneSession={cloneSession}
                            isPseudocode={false}
                            onLuaScriptChange={onLuaScriptChange}
                            showScreen={showScreen}
                            setShowScreen={setShowScreen}
                            isAuthed={isAuthed}
                            loadingSessionId={loadingSessionId}
                            onCancelLoadSession={() => {
                                loadingSessionRef.current = null;
                                setLoadingSessionId(null);
                                dispatch(setScriptMessage([]));
                                dispatch(setProcessing(false));
                                dispatch(setSessionId(''));
                                dispatch(setSessionDataset(''));
                                dispatch(setSessionMode(''));
                                dispatch(setIsViewingOthersSession(false));
                                setShowScreen(true);
                            }}
                            onRetryAnalytics={(message: any, validationReasoning: string) => {
                                const responseIndex = settings.chat.indexOf(message);
                                if (responseIndex < 0) return;

                                let userMessageIndex = -1;
                                for (let i = responseIndex - 1; i >= 0; i--) {
                                    if ((settings.chat[i] as any).role === 'User') {
                                        userMessageIndex = i;
                                        break;
                                    }
                                }
                                if (userMessageIndex < 0) return;

                                const userInput = (settings.chat[userMessageIndex] as any).userInput ?? '';
                                const previousScript = message.script ?? '';

                                // Preserve only the conversation preceding the selected exchange,
                                // then provide the validator's concern as user-supplied retry context.
                                const historyBeforeExchange = settings.chat
                                    .slice(0, userMessageIndex)
                                    .filter((m: any) => m.role === 'User' || m.role === 'Assistant')
                                    .map((m: any) => m.role === 'User'
                                        ? { role: 'user', content: m.userInput || '' }
                                        : { role: 'assistant', content: m.responseText || '' }
                                    );
                                const scriptContext = previousScript
                                    ? `\n\nThe previous script was:\n\`\`\`lua\n${previousScript}\n\`\`\``
                                    : '';
                                const retryHistory = [
                                    ...historyBeforeExchange,
                                    { role: 'user', content: `The previous script ran successfully but its results were flagged as suspicious: ${validationReasoning}${scriptContext}\n\nPlease identify what may have caused this and regenerate the script.` },
                                ];
                                const datasetForRetry = settings.sessionId
                                    ? (settings.sessionDataset || settings.dataset)
                                    : settings.dataset;

                                dispatch(setProcessing(true));
                                copilotService.AnalyticsAPI(
                                    userInput,
                                    settings.apiEndpoint,
                                    datasetForRetry,
                                    retryHistory,
                                    undefined,
                                    settings.sessionId || undefined,
                                    (description, script) => {
                                        dispatch(addPseudoMessage({
                                            role: 'Assistant',
                                            sessionId: settings.sessionId,
                                            endpoint: 'analytics',
                                            responseText: `**Retry approach:** ${description}\n\n*Executing analysis across all shards...*`,
                                            unparsedGptResponse: '',
                                            script: script,
                                        } as any));
                                    }
                                ).then((response) => {
                                    if (response.sessionId) dispatch(setSessionId(response.sessionId));
                                    handlePseudocodeReturn(userInput, response);
                                }).catch((error: unknown) => {
                                    const errorMessage = error instanceof Error
                                        ? error.message
                                        : typeof error === 'string'
                                            ? error
                                            : 'An unexpected error occurred';
                                    dispatch(addPseudoMessage({
                                        role: 'Assistant',
                                        sessionId: settings.sessionId,
                                        endpoint: 'analytics',
                                        status: 500,
                                        responseText: `Retry failed: ${errorMessage}`,
                                        unparsedGptResponse: '',
                                    }));
                                }).finally(() => {
                                    dispatch(setProcessing(false));
                                });
                            }}
                        />
                    </div>
                    <div className={classes.footer}>
                        <Text>
                            The Gaming Segmentation Copilot can make mistakes.
                            Consider checking important information. Note: GSC
                            data is expected to have a latency of 3-5 days.
                        </Text>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default CampaignGPT;
